import React from 'react';
import {useTranslation} from 'react-i18next';
import {PANEL_TYPES} from '../state/informeModel';
import {defaultTitleKey} from '../state/panelLayout';
import {panelTitle} from '../state/fieldLabels';
import {InlineEditableTitle} from './InlineEditableTitle';
import {GripIcon} from './AddPanelMenu';
import {FieldHelpIcon} from '../../elements/FieldHelpIcon';
import {hasFieldDescription} from '../../../utils/fieldDescriptions';

/**
 * El «?» de una métrica destacada (E8): la misma ayuda que el modal de columnas, para que «Valor
 * de compra» o «ROAS» se expliquen donde más se miran — también en el portal, que es donde los lee
 * el cliente. Sólo con texto propio (`FIELD_DESCRIPTIONS`) o con la descripción de un personalizado
 * de GA4; si no hay nada que decir, no hay ícono.
 */
export function kpiHelp(panel, fieldsData) {
    if (!panel || panel.type !== PANEL_TYPES.KPI) return null;
    const metric = (panel.metrics || [])[0];
    if (!metric) return null;
    const field = fieldsData && fieldsData.FIELD_DATA_BY_ID && fieldsData.FIELD_DATA_BY_ID[metric];
    const customDescription = field && field.ga4_custom ? field.description : null;
    if (!hasFieldDescription(metric) && !customDescription) return null;
    // `data-pdf-hide`: en el PDF el «?» no abre nada (`pdfExport.SCREEN_ONLY_SELECTORS`).
    return (
        <span className="inf-panel-help" data-testid="kpi-help" data-pdf-hide="">
            <FieldHelpIcon fieldId={metric} description={customDescription}/>
        </span>
    );
}

/**
 * El marco de todo panel del folio: grip (arrastrar), título editable con un clic, slot de
 * controles, ✕ en hover (quitar, con Deshacer en el toast) y el cuerpo. En el portal
 * (`readOnly`) no hay grip, ni ✕, ni edición.
 *
 * El título de un panel sin nombre propio se resuelve distinto según quién mire: en el editor
 * el placeholder genérico invita a escribir (y el select de al lado ya dice la métrica), pero
 * en el portal el cliente sólo ve el título, así que ahí manda el nombre de la métrica
 * (QA FE-05: dos KPI que decían «Métrica destacada» y un número).
 *
 * Excepción del KPI en el editor (paridad con el mockup): su encabezado NO es un título
 * editable con un select al lado, sino el select SOLO, estilado como el rótulo chico y gris
 * del mockup. Poner las dos cosas mostraba «Métrica destacada» y, al lado, la métrica: el
 * rótulo genérico no agregaba nada y le robaba el aire al número, que es lo que se lee.
 */
export function PanelShell({panel, state, controls, children, dragHandleProps, onRemove, onRename,
                            readOnly = false, panelRef = null, fieldsData = null, accentIndex = null,
                            headerWarnings = null}) {
    const {t} = useTranslation();
    const isText = panel.type === PANEL_TYPES.TEXT;
    // En un bloque de texto el título es opcional, y el placeholder lo dice: el genérico («Texto»)
    // haría creer que hace falta escribir uno.
    const genericPlaceholder = isText
        ? t('informes.panel.text.title_placeholder', 'Título (opcional)')
        : t(defaultTitleKey(panel.type), panel.type);
    const resolved = panelTitle(panel, fieldsData, t);
    const placeholder = readOnly ? (resolved || genericPlaceholder) : genericPlaceholder;
    // En sólo lectura el título resuelto ES el título: no hay nada que editar y el gris de
    // placeholder (2,77:1) no puede ser el rótulo real que lee el cliente.
    const value = readOnly ? (resolved || '') : panel.title;
    // El KPI del editor: el select de métrica ocupa el lugar del título (ver docstring).
    const metricAsTitle = !readOnly && panel.type === PANEL_TYPES.KPI && !!controls;
    const help = kpiHelp(panel, fieldsData);
    // Un texto sin título, en sólo lectura, no lleva encabezado: el párrafo arranca arriba (M4).
    const showHeader = !(readOnly && isText && !resolved);
    // En el editor sí lo lleva (ahí se escribe el título), pero el PDF lo fotografía: sin esto, cada
    // texto sin título salía con el gris «Título (opcional)» arriba (QA-texto-N01). En el encabezado
    // de un texto no hay otra cosa: el grip y la ✕ ya no salen en el PDF.
    const pdfHideHeader = isText && !(panel.title || '').trim();
    return (
        <section ref={panelRef}
                 className={`inf-panel inf-panel--${panel.type} ${readOnly ? '-readonly' : ''} ${accentIndex === null ? '' : `inf-panel--accent-${accentIndex % 6}`}`}
                 data-panel-uid={panel.uid} data-state={state || 'OK'}>
            {showHeader && <header className="inf-panel-header" data-pdf-hide={pdfHideHeader ? '' : undefined}>
                {!readOnly && dragHandleProps && (
                    <span className="inf-panel-grip" {...dragHandleProps}
                          title={t('informes.panel.drag', 'Arrastrar para reordenar')}
                          aria-label={t('informes.panel.drag', 'Arrastrar para reordenar')}>
                        <GripIcon/>
                    </span>
                )}
                {/* Los avisos del bloque (el ⚠ de eventos, el ⓘ de un dato aproximado) van PEGADOS A
                    LA DERECHA del título, en todo tipo de bloque (QA `QA-VIS-infev-02`). Sueltos en
                    el header quedaban en tres lugares distintos: el título crece con `flex: 1` y los
                    empujaba contra los selectores, y en la KPI —cuyo título es el select de métrica—
                    caían ANTES del título. Por eso el título y los avisos comparten
                    `.inf-panel-heading`, y en la KPI van dentro del select-título. */}
                {!metricAsTitle && (
                    <div className="inf-panel-heading">
                        <InlineEditableTitle value={value} placeholder={placeholder} readOnly={readOnly}
                                             onCommit={(title) => onRename && onRename(title)}/>
                        {help}
                        {headerWarnings}
                    </div>
                )}
                {controls && (
                    <div className={`inf-panel-controls ${metricAsTitle ? '-as-title' : ''}`}>
                        {controls}
                        {metricAsTitle && help}
                        {metricAsTitle && headerWarnings}
                    </div>
                )}
                {!readOnly && onRemove && (
                    <button type="button" className="inf-panel-remove" onClick={onRemove}
                            title={t('informes.panel.remove', 'Quitar de la plantilla')}
                            aria-label={t('informes.panel.remove', 'Quitar de la plantilla')}>
                        ✕
                    </button>
                )}
            </header>}
            <div className="inf-panel-body">{children}</div>
        </section>
    );
}
